/**
 * Zustand store for managing Newsquawk headlines and search results.
 * Maintains consistency between headlines and searchResults collections,
 * ensuring mutating operations like updates or deletions apply to both collections.
 */
import { Headline } from "@/widget-components/newsquawk-headlines/models/Headline";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { diagnosticEventLog } from "@/shared/services/diagnostic";
import { useBrokerStore } from "@/stores/BrokerStore";

/**
 * Initial state for the headlines store
 */
const initialState = {
  headlines: [],
  searchResults: [],
};

type HeadlineStore = {
  /** Array of headline items */
  headlines: Headline[];

  /** Array of search result headline items */
  searchResults: Headline[];

  /**
   * Set headlines array, optionally appending to existing headlines
   * @param headlines - Array of headlines to set
   * @param push - If true, appends to existing headlines instead of replacing
   */
  setHeadlines: (headlines: Headline[], push?: boolean) => void;

  /** Add a single headline to the beginning of the headlines array */
  addHeadline: (headline: Headline) => void;

  /** Add a headline only if it doesn't already exist in the headlines array
   * @returns true if the headline was added, false if it already existed
   */
  addHeadlineIfNotExists: (headline: Headline) => boolean;

  /** Set search results array */
  setSearchResults: (searchResults: Headline[]) => void;

  /** Add a single search result to the beginning of search results array */
  addSearchResult: (searchResult: Headline) => void;

  /** Add a search result only if it doesn't already exist in the search results array
   * @returns true if the search result was added, false if it already existed
   */
  addSearchResultIfNotExists: (searchResult: Headline) => boolean;

  /** Remove a headline from both headlines and searchResults arrays */
  removeHeadline: (headline: Headline) => void;

  /** Update a headline in both headlines and searchResults arrays */
  updateHeadline: (headline: Headline) => void;

  /** Remove a headline by ID from both headlines and searchResults arrays */
  removeHeadlineById: (id: number) => void;
};

/**
 * Zustand store for managing headlines and search results
 * Includes devtools middleware for debugging
 */
const useHeadlineStore = create<HeadlineStore>()(
  devtools((set, get) => ({
    ...initialState,

    addHeadline: (headline: Headline) => {
      set(
        (state) => ({
          headlines: [headline, ...state.headlines],
        }),
        undefined,
        "headlines/addHeadline"
      );
      // Broker widgets in "follow feed" auto-switch mode track arriving tags
      // (no-op unless a broker widget is mounted with the mode enabled).
      useBrokerStore.getState().publishTags("feed", headline.tags);
    },

    addHeadlineIfNotExists: (headline: Headline) => {
      const isExist = get().headlines.some((h) => h.id === headline.id);

      set(
        (state) => ({
          // Only add if headline with same ID doesn't exist
          headlines: isExist ? state.headlines : [headline, ...state.headlines],
        }),
        undefined,
        "headlines/addHeadlineIfNotExists"
      );

      if (!isExist) {
        useBrokerStore.getState().publishTags("feed", headline.tags);
      }

      // Return true if headline was added, false if it already existed

      return !isExist;
    },

    setHeadlines: (headlines: Headline[], push = false) =>
      push
        ? set(
            (state) => ({
              headlines: [...state.headlines, ...headlines],
            }),
            undefined,
            "headlines/setHeadlines"
          )
        : set({ headlines }, undefined, "headlines/setHeadlines"),

    removeHeadline: (headline: Headline) =>
      set(
        (state) => ({
          headlines: state.headlines.filter((h) => h.id !== headline.id),
          searchResults: state.searchResults.filter(
            (h) => h.id !== headline.id
          ),
        }),
        undefined,
        "headlines/removeHeadline"
      ),

    removeHeadlineById: (id: number) => {
      set(
        (state) => ({
          // Filter out the headline with matching ID from both arrays
          headlines: state.headlines.filter((h) => h.id != id),
          searchResults: state.searchResults.filter((h) => h.id != id),
        }),
        undefined,
        "headlines/removeHeadlineById"
      );
    },

    updateHeadline: (headline: Headline) =>
      set(
        (state) => ({
          headlines: state.headlines.map((h) =>
            h.id == headline.id ? headline : h
          ),
          searchResults: state.searchResults.map((h) =>
            h.id == headline.id ? headline : h
          ),
        }),
        undefined,
        "headlines/updateHeadline"
      ),

    addSearchResult: (headline: Headline) => {
      set(
        (state) => ({ searchResults: [headline, ...state.searchResults] }),
        undefined,
        "searchResults/addSearchResult"
      );
    },

    addSearchResultIfNotExists: (headline: Headline) => {
      const isExist = get().searchResults.some((h) => h.id === headline.id);

      set(
        (state) => ({
          // Only add if search result with same ID doesn't exist
          searchResults: isExist ? state.searchResults : [headline, ...state.searchResults],
        }),
        undefined,
        "searchResults/addSearchResultIfNotExists"
      );

      // Return true if search result was added, false if it already existed
      return !isExist;
    },

    setSearchResults: (headlines: Headline[]) => {
      set(
        { searchResults: headlines },
        undefined,
        "searchResults/setSearchResults"
      );
    },
  }))
);

// Record headline count changes for diagnostic reports
useHeadlineStore.subscribe((state, prevState) => {
  if (state.headlines.length !== prevState.headlines.length) {
    diagnosticEventLog.record("headlines:widget", "info", "headline-count-change", {
      count: state.headlines.length,
      previousCount: prevState.headlines.length,
    });
  }
});

export default useHeadlineStore;
